import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  MENTORSHIP_LEAD_TITLE,
  buildMentorshipDetailRows,
  formatMentorshipMoney,
  getMentorshipPreferencesFromLead,
  isMentorshipLead,
} from 'lib/mentorship-leads';
import Link from 'next/link';

type MentorshipLeadDetailsProps = {
  lead: {
    content?: Record<string, any>;
    metadata?: Record<string, any>;
  };
};

export default function MentorshipLeadDetails({
  lead,
}: MentorshipLeadDetailsProps) {
  if (!isMentorshipLead(lead)) {
    return null;
  }

  const preferences = getMentorshipPreferencesFromLead(lead);
  const rows = buildMentorshipDetailRows(preferences);
  const estimateLines = preferences?.estimate_lines ?? [];
  const courses = lead.metadata?.courses ?? [];
  const message = lead.content?.message?.trim() || '';

  return (
    <div className="sm:col-span-2">
      <dt className="text-sm font-medium text-gray-500 mb-4">
        {MENTORSHIP_LEAD_TITLE}
      </dt>
      <dd className="mt-1 space-y-4">
        {rows.length > 0 ? (
          <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {rows.map((row) => (
                <div
                  key={row.label}
                  className={row.multiline ? 'sm:col-span-2' : undefined}
                >
                  <div className="text-xs font-semibold uppercase tracking-wide text-teal-700">
                    {row.label}
                  </div>
                  <div className="mt-1 text-sm text-gray-900">{row.value}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {estimateLines.length > 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Estimate breakdown
            </div>
            <ul className="mt-3 space-y-2">
              {estimateLines.map((line) => (
                <li
                  key={line.label}
                  className="flex items-start justify-between gap-3 text-sm"
                >
                  <span className="text-gray-700">{line.label}</span>
                  <span className="font-semibold text-gray-900">
                    {formatMentorshipMoney(line.amount_pence)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {courses.length > 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Selected courses
            </div>
            <ul className="mt-3 space-y-2">
              {courses.map(
                (course: { id?: string; title?: string; price?: string }) => (
                  <li
                    key={course.id || course.title}
                    className="text-sm text-gray-800"
                  >
                    {course.title}
                    {course.price ? (
                      <span className="text-gray-500">{` · £${course.price}`}</span>
                    ) : null}
                  </li>
                ),
              )}
            </ul>
          </div>
        ) : null}

        {message ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Enquiry message
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">
              {message}
            </p>
          </div>
        ) : null}

        {preferences?.source_page ? (
          <Link
            href={`https://cosmetic.college${preferences.source_page}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center text-sm font-medium text-teal-700 hover:text-teal-800"
          >
            Open mentorship page
            <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" />
          </Link>
        ) : null}
      </dd>
    </div>
  );
}
